import { signalStore, withState, withMethods, patchState } from '@ngrx/signals';

/**
 * Per-user, per-view table column preferences (visibility and column order).
 * The order of the persisted `TableColInfo[]` is the user's column order.
 */
export interface TablePreferencesState {
    columnVisibility: { [key: string]: TableColInfo[] };
}

const initialState: TablePreferencesState = {
    columnVisibility: {},
};

interface TableColInfo {
    columnName: string;
    hidden: boolean;
}

function storageKey(uid: string, configKey: string): string {
    return `${uid}-column-data-new-${configKey}`;
}

function getActiveColumns(colInfos: TableColInfo[]): string[] {
    return colInfos
        .filter(colInfo => !colInfo.hidden)
        .map(colInfo => colInfo.columnName);
}

/**
 * Builds the persisted column infos. The order of the array is the column
 * order: the visible columns come first in the given order, the remaining
 * available columns are appended as hidden.
 */
function buildColInfos(
    visibleColumns: string[],
    availableColumns: string[]
): TableColInfo[] {
    const visible = visibleColumns.filter(col =>
        availableColumns.includes(col)
    );
    const hidden = availableColumns.filter(col => !visible.includes(col));
    return [
        ...visible.map(col => ({ columnName: col, hidden: false })),
        ...hidden.map(col => ({ columnName: col, hidden: true })),
    ];
}

function updateColInfo(
    oldColInfos: TableColInfo[],
    availableColumns: string[]
): TableColInfo[] {
    const colInfos = oldColInfos.filter(colInfo =>
        availableColumns.includes(colInfo.columnName)
    );
    availableColumns.forEach(col => {
        if (
            colInfos.find(colInfo => colInfo.columnName === col) === undefined
        ) {
            colInfos.push({ columnName: col, hidden: false });
        }
    });
    return colInfos;
}

export const TablePreferencesStore = signalStore(
    { providedIn: 'root' },
    withState<TablePreferencesState>(initialState),
    withMethods(store => ({
        setVisibleColumns(
            uid: string,
            configKey: string,
            visibleColumns: string[],
            availableColumns: string[]
        ) {
            const colInfos = buildColInfos(visibleColumns, availableColumns);
            localStorage.setItem(
                storageKey(uid, configKey),
                JSON.stringify(colInfos)
            );
            patchState(store, state => ({
                columnVisibility: {
                    ...state.columnVisibility,
                    [configKey]: colInfos,
                },
            }));
        },

        getVisibleColumns(
            uid: string,
            configKey: string,
            availableColumns: string[]
        ): string[] {
            const cached = store.columnVisibility()[configKey];
            if (cached) {
                return getActiveColumns(cached);
            }
            let colInfos: TableColInfo[] = [];
            const data = localStorage.getItem(storageKey(uid, configKey));
            if (data) {
                colInfos = JSON.parse(data) as TableColInfo[];
            }
            colInfos = updateColInfo(colInfos, availableColumns);
            return getActiveColumns(colInfos);
        },

        clearVisibleColumns(uid: string, configKey: string): void {
            localStorage.removeItem(storageKey(uid, configKey));
            patchState(store, state => {
                const newColumnVisibility = {
                    ...state.columnVisibility,
                };
                delete newColumnVisibility[configKey];
                return {
                    columnVisibility: newColumnVisibility,
                };
            });
        },
    }))
);
